import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { http } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { fDelta, fDia, fHora, fValor, flecha, tonoCls } from "../../lib/trading";
import { Detalle } from "./Detalle";
import "../../styles/trading.css";

const POLL = 5 * 60 * 1000;
const llave = (u) => `trf_ticker_min:${u?.id || "anon"}`;

const Item = ({ it, onOpen, dup }) => (
  <button type="button" data-tk-item onClick={(e) => onOpen(it, e.clientX)} tabIndex={dup ? -1 : 0} aria-hidden={dup || undefined}
    className="tk-item text-[11px] sm:text-xs" data-testid={dup ? undefined : `tk-item-${it.key}`}>
    <span className="font-semibold tracking-wide text-[var(--trd-muted)]">{it.label}</span>
    <span className="trd-mono font-medium text-[var(--trd-text)]">{it.sin_docs ? "Sin documentos" : fValor(it.valor, it.fmt)}</span>
    <span className={`trd-mono ${tonoCls(it)}`} data-testid={dup ? undefined : `tk-delta-${it.key}`} data-tono={it.prev == null || it.dir === "eq" ? "flat" : it.tono}>{flecha(it.dir)} {fDelta(it)}</span>
  </button>
);

export const Cintilla = ({ portal = false }) => {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [min, setMin] = useState(() => localStorage.getItem(llave(user)) === "1");
  const [sel, setSel] = useState(null);
  const [touch, setTouch] = useState(false);
  const ver = useRef(null);
  const t = useRef(null);
  const cargar = useCallback(() => http.get("/ticker").then((r) => { setD(r.data); ver.current = r.data.publicado?.at; }).catch(() => {}), []);
  useEffect(() => { cargar(); const id = setInterval(() => http.get("/ticker/version").then((r) => r.data.at && r.data.at !== ver.current && cargar()).catch(() => {}), POLL); return () => clearInterval(id); }, [cargar]);
  useEffect(() => { setMin(localStorage.getItem(llave(user)) === "1"); }, [user]);
  useEffect(() => { document.documentElement.classList.toggle("tk-min", min); return () => document.documentElement.classList.remove("tk-min"); }, [min]);
  const toggle = () => { const v = !min; localStorage.setItem(llave(user), v ? "1" : "0"); setMin(v); setSel(null); };
  const pausa = () => { clearTimeout(t.current); setTouch(true); };
  const sigue = () => { clearTimeout(t.current); t.current = setTimeout(() => setTouch(false), 3000); };
  const items = d?.items || [];
  const pub = d?.publicado;
  const viejo = d?.corte && pub && d.corte < pub.fecha;
  if (min) return (
    <div className="trd fixed inset-x-0 top-0 z-[45] h-[var(--tk-h)]" data-testid="cintilla" data-min="1">
      <button onClick={toggle} className="absolute right-2 top-0 flex h-[18px] items-center gap-1 rounded-b-md bg-[var(--trd-bg)] px-2 text-[10px] font-semibold text-[var(--trd-muted)] hover:text-white" data-testid="tk-expandir" aria-label="Mostrar cintilla"><ChevronDown size={11} />Cintilla</button>
    </div>
  );
  return (
    <div className="trd fixed inset-x-0 top-0 z-[45] flex h-[var(--tk-h)] items-stretch border-b border-[var(--trd-border)] text-[11px]" data-testid="cintilla" data-min="0" role="region" aria-label="Cintilla de indicadores">
      <div className="flex shrink-0 items-center gap-1.5 border-r border-[var(--trd-border)] px-2 sm:px-3" data-testid="tk-label">
        <span className="font-bold tracking-widest text-[var(--trd-accent)]"><span className="sm:hidden">TRF</span><span className="hidden sm:inline">TRAFALGAR</span></span>
        {pub && <span className="trd-mono text-[var(--trd-muted)]" data-testid="tk-publicado">· <span className="hidden md:inline">Publicado </span>{fHora(pub.at)}{d.vs && <> · vs {fDia(d.vs)}</>}</span>}
        {viejo && <span className="hidden text-[10px] text-[var(--trd-muted)] lg:inline" data-testid="tk-corte">· Cartera al corte {fDia(d.corte, true)}</span>}
      </div>
      <div className={`tk-viewport min-w-0 flex-1 ${touch || sel ? "tk-paused" : ""}`} onTouchStart={pausa} onTouchEnd={sigue} data-testid="tk-viewport" style={{ "--tk-dur": `${Math.max(40, items.length * 7)}s` }}>
        <div className="tk-track h-full" data-testid="tk-track">
          {[0, 1].map((c) => <div key={c} className={`flex h-full ${c ? "tk-dup" : ""}`} aria-hidden={c ? true : undefined}>
            {items.map((it) => <Item key={it.key + c} it={it} dup={!!c} onOpen={(x, cx) => setSel({ it: x, x: cx })} />)}
            {!items.length && !c && <span className="flex items-center px-4 text-[var(--trd-muted)]" data-testid="tk-vacio">{d ? "Sin publicación todavía" : "Cargando indicadores…"}</span>}
          </div>)}
        </div>
      </div>
      <button onClick={toggle} className="shrink-0 border-l border-[var(--trd-border)] px-2 text-[var(--trd-muted)] hover:text-white" data-testid="tk-minimizar" aria-label="Minimizar cintilla" title="Minimizar"><ChevronUp size={13} /></button>
      {sel && <Detalle it={sel.it} vs={d.vs} x={sel.x} portal={portal} onClose={() => setSel(null)} />}
    </div>
  );
};
